<template>
  <div class="ga-admin-page">
    <AdminHeader
      :title="$t('ga_admin_customers_title')"
      :subtitle="$t('ga_admin_customers_subtitle')"
      compact
    />

    <div class="page-body">
      <div v-if="loading" class="loading-card">
        <div class="loading-spinner"></div>
        <div class="loading-text">{{ $t('ga_admin_loading_customers') }}</div>
      </div>
      <div v-else-if="errorMessage" class="state-card warning">{{ errorMessage }}</div>
      <template v-else>
        <nav class="customer-type-tabs" role="tablist" :aria-label="$t('ga_admin_customer_type_tabs')">
          <button
            v-for="option in tabs"
            :key="option.value"
            type="button"
            :class="['customer-type-tab', { active: activeTab === option.value }]"
            role="tab"
            :aria-selected="activeTab === option.value"
            @click="activeTab = option.value"
          >
            <span class="tab-label">{{ option.label }}</span>
            <span class="tab-count">{{ option.countText }}</span>
          </button>
        </nav>

        <section class="section-card panorama-card">
          <div class="section-head">
            <div>
              <div class="section-title">{{ currentTitle }}</div>
              <div class="section-desc">{{ currentDesc }}</div>
            </div>
            <button class="ghost-btn ai-action-btn" @click="openAi(currentPrompt)">
              <font-awesome-icon :icon="['far', 'lightbulb']" class="ai-btn-icon" />
              <span>{{ $t('ga_admin_ai_interpret') }}</span>
            </button>
          </div>

          <div class="panorama-summary">
            <div class="summary-text">{{ currentSummary }}</div>
            <div class="summary-metrics">
              <div v-for="metric in currentMetrics" :key="metric.label" class="metric-chip">
                <span class="metric-label">{{ metric.label }}</span>
                <span class="metric-value">{{ metric.value }}</span>
              </div>
            </div>
            <div class="summary-source-row">
              <AISourceTag />
            </div>
          </div>
        </section>

        <section class="section-card">
          <div class="section-head">
            <div>
              <div class="section-title">{{ currentListTitle }}</div>
              <div class="section-desc">{{ currentListDesc }}</div>
            </div>
          </div>

          <GbaList
            :key="`gba-list-${activeTab}`"
            embedded-mode
            :sync-route-tab="false"
            :forced-active-tab="gbaListActiveTab"
            :external-items="currentCustomers"
          />
        </section>
      </template>
    </div>
  </div>
</template>

<script>
import { Icon } from 'vant';
import AISourceTag from '@/components/AISourceTag';
import AdminHeader from './components/AdminHeader.vue';
import {loadGaAdminAnalytics} from './analytics';
import GbaList from '@/views/gba/list.vue';

export default {
  name: 'GAAdminCustomers',
  components: {
    AISourceTag,
    AdminHeader,
    GbaList,
    [Icon.name]: Icon,
  },
  data() {
    return {
      activeTab: 'clinical',
      loading: false,
      errorMessage: '',
      analytics: {
        customerPanoramas: {
          clinical: {summary: '', prompt: '', metrics: []},
          expert: {summary: '', prompt: '', metrics: []},
        },
        raw: {
          clinicalCustomers: [],
          expertCustomers: [],
        },
      },
    };
  },
  computed: {
    tabs() {
      const clinicalCount = this.analytics.raw.clinicalCustomers?.length || 0;
      const expertCount = this.analytics.raw.expertCustomers?.length || 0;
      return [
        {
          value: 'clinical',
          label: this.$t('clinical_doctor'),
          countText: this.$t('ga_admin_count_customer', {count: clinicalCount}),
        },
        {
          value: 'expert',
          label: this.$t('he_expert'),
          countText: this.$t('ga_admin_count_expert', {count: expertCount}),
        },
      ];
    },
    gbaListActiveTab() {
      return this.activeTab === 'expert' ? '1' : '0';
    },
    currentTitle() {
      if (this.activeTab === 'clinical') return this.$t('ga_admin_customers_clinical_title');
      return this.$t('ga_admin_customers_expert_title');
    },
    currentDesc() {
      if (this.activeTab === 'clinical') return this.$t('ga_admin_customers_clinical_desc');
      return this.$t('ga_admin_customers_expert_desc');
    },
    currentPrompt() {
      return this.currentPanorama.prompt || (
        this.activeTab === 'clinical'
          ? this.$t('ga_admin_customers_clinical_prompt')
          : this.$t('ga_admin_customers_expert_prompt')
      );
    },
    currentSummary() {
      return this.currentPanorama.summary || '';
    },
    currentMetrics() {
      return this.currentPanorama.metrics || [];
    },
    currentPanorama() {
      const key = this.activeTab === 'clinical' ? 'clinical' : 'expert';
      return this.analytics.customerPanoramas[key] || {};
    },
    currentCustomers() {
      return this.activeTab === 'clinical'
        ? this.analytics.raw.clinicalCustomers
        : this.analytics.raw.expertCustomers;
    },
    currentListTitle() {
      if (this.activeTab === 'clinical') return this.$t('ga_admin_customers_clinical_list_title');
      return this.$t('ga_admin_customers_expert_list_title');
    },
    currentListDesc() {
      if (this.activeTab === 'clinical') return this.$t('ga_admin_customers_clinical_list_desc');
      return this.$t('ga_admin_customers_expert_list_desc');
    },
  },
  watch: {
    '$i18n.locale'() {
      this.fetchAnalytics();
    },
  },
  created() {
    this.fetchAnalytics();
  },
  methods: {
    async fetchAnalytics() {
      this.loading = true;
      this.errorMessage = '';
      try {
        const analytics = await loadGaAdminAnalytics();
        this.analytics = {
          ...this.analytics,
          ...analytics,
          raw: {
            ...this.analytics.raw,
            ...(analytics.raw || {}),
          },
        };
      } catch (e) {
        console.error(e);
        this.errorMessage = this.$t('ga_admin_error_customers');
      } finally {
        this.loading = false;
      }
    },
    openAi(message) {
      this.$root.$emit('open-ai-chat', message, true, 'SRCopilotInsightGBA');
    },
  },
};
</script>

<style scoped lang="scss">
.ga-admin-page {
  min-height: 100%;
  background-color: #031b13;
  background:
    linear-gradient(122deg, transparent 0 16%, rgba(18, 83, 53, 0.56) 16% 29%, transparent 29% 100%),
    linear-gradient(122deg, transparent 0 60%, rgba(10, 58, 38, 0.72) 60% 76%, transparent 76% 100%),
    linear-gradient(180deg, #064527 0%, #003d25 34%, #031b13 100%);
}

.page-body {
  padding: 12px 12px 90px;
}

.state-card {
  margin-bottom: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #fff;
  color: #666;
  font-size: 13px;
  line-height: 1.5;
}

.state-card.warning {
  color: #00b852;
}

.loading-card {
  min-height: 240px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  border-radius: 16px;
  background: #fff;
  color: #666;
  font-size: 13px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.loading-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid #f3d8d2;
  border-top-color: #00b852;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.section-card {
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.section-card {
  margin-bottom: 12px;
  padding: 15px;
}

.panorama-card {
  overflow: hidden;
}

.customer-type-tabs {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px;
  margin-bottom: 12px;
  padding: 4px;
  border: 1px solid rgba(0, 184, 82, 0.08);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.72);
}

.customer-type-tab {
  position: relative;
  min-width: 0;
  min-height: 48px;
  padding: 8px 6px 9px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: #6b7280;
  text-align: center;
}

.customer-type-tab.active {
  background: #fff;
  color: #00b852;
  box-shadow: 0 4px 12px rgba(0, 184, 82, 0.1);
}

.customer-type-tab.active::after {
  position: absolute;
  right: 20%;
  bottom: 5px;
  left: 20%;
  height: 2px;
  border-radius: 999px;
  background: #00b852;
  content: '';
}

.tab-label,
.tab-count {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tab-label {
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
}

.tab-count {
  margin-top: 2px;
  color: #8b95a5;
  font-size: 10px;
  line-height: 14px;
}

.customer-type-tab.active .tab-count {
  color: #b75f4b;
}

.ghost-btn {
  border: 1px solid transparent;
  border-radius: 999px;
  padding: 9px 14px;
  font-size: 12px;
}

.section-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.section-title {
  font-size: 17px;
  font-weight: 700;
  color: #333;
}

.section-desc {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.5;
  color: #999;
}

.panorama-summary {
  margin-top: 14px;
  padding-top: 2px;
}

.summary-text {
  font-size: 14px;
  line-height: 1.7;
  color: #4a403d;
}

.summary-metrics {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-top: 12px;
}

.metric-chip {
  min-width: 0;
  padding: 9px 10px;
  border-radius: 10px;
  background: #f8fbf9;
}

.metric-label {
  display: block;
  font-size: 11px;
  color: #8a7b76;
}

.metric-value {
  display: block;
  margin-top: 4px;
  font-size: 15px;
  font-weight: 700;
  color: #333;
}

.summary-source-row {
  margin-top: 8px;
  text-align: right;
}

.ghost-btn {
  flex-shrink: 0;
  border-color: #00b852;
  background: #fff;
  color: #00b852;
}

.ai-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 0;
  background: linear-gradient(135deg, #006b4f 0%, #008f72 48%, #00b852 100%);
  color: #fff;
  box-shadow: 0 6px 14px rgba(0, 184, 82, 0.2);
}

.ai-btn-icon {
  font-size: 13px;
}

</style>
